import {FC, useMemo} from 'react'
import {
  getJobSpan,
  isPhysicalPrintJob,
  isPrefetchPrintJob,
} from '../mappers/fromPrintJobs'
import {getMishapInterval, isOfflineMishap, isStatusMishap} from '../mappers/fromMishaps'
import {PrintStoryDoc, PrintStoryEvent} from '../types'
import {formatDuration, formatElapsedSince, formatTimestamp} from '../time'
import {jobStatusBarColor, severityBarColor} from '../severity'

type Props = {
  events: PrintStoryEvent[]
  printJobs: PrintStoryDoc[]
  mishaps?: PrintStoryDoc[]
  onSelectJob?: (jobId: string) => void
  /** When true, time scale ends at the latest real activity (no extension to "now"). */
  endAtLastEvent?: boolean
}

type LaneRow = {
  key: string
  label: string
  subLabel?: string
  group: 'lifecycle' | 'prefetch' | 'print' | 'health'
  bars: Array<{
    startMs: number
    endMs: number
    color: string
    title: string
    ongoing?: boolean
    jobId?: string
  }>
  points: Array<{
    ts: number
    color: string
    title: string
  }>
}

const GROUP_META: Record<
  LaneRow['group'],
  {title: string; badge: string; description: string}
> = {
  lifecycle: {
    title: 'Lifecycle',
    badge: 'badge-light-primary',
    description: 'Content, code, mail, and render markers',
  },
  prefetch: {
    title: 'Prefetch',
    badge: 'badge-light-info',
    description: 'One lane per targeted device',
  },
  print: {
    title: 'Print',
    badge: 'badge-light-success',
    description: 'One lane per print job / split copy',
  },
  health: {
    title: 'Printer health',
    badge: 'badge-light-danger',
    description: 'Mishap intervals overlapping this window',
  },
}

const LEGEND = [
  {label: 'Completed', color: '#50cd89'},
  {label: 'In flight', color: '#ffc700'},
  {label: 'Failed / blocked', color: '#f1416c'},
  {label: 'Offline', color: '#b02a37'},
]

export const ContentStoryTimeline: FC<Props> = ({
  events,
  printJobs,
  mishaps = [],
  onSelectJob,
  endAtLastEvent = false,
}) => {
  const {rows, minTs, maxTs, originTs} = useMemo(() => {
    const prefetchJobs = printJobs.filter(isPrefetchPrintJob)
    const physicalJobs = printJobs.filter(isPhysicalPrintJob)

    const pointLanes: LaneRow[] = [
      {
        key: 'content-code',
        label: 'Content / Code',
        subLabel: 'Created · assigned · mail',
        group: 'lifecycle',
        bars: [],
        points: events
          .filter((e) => e.lane === 'content' || e.lane === 'code' || e.lane === 'mail')
          .map((e) => ({
            ts: e.ts,
            color: severityBarColor(e.severity),
            title: e.title,
          })),
      },
      {
        key: 'render',
        label: 'Render',
        subLabel: 'Composite / printable',
        group: 'lifecycle',
        bars: [],
        points: events
          .filter((e) => e.lane === 'render')
          .map((e) => ({
            ts: e.ts,
            color: severityBarColor(e.severity),
            title: e.title,
          })),
      },
    ]

    const prefetchRows: LaneRow[] = prefetchJobs.map((job) => {
      const span = getJobSpan(job)
      const deviceId = String(job.deviceId || job.jobId || job.id)
      return {
        key: `prefetch-${deviceId}-${job.id}`,
        label: deviceId,
        subLabel: 'Prefetch',
        group: 'prefetch',
        bars: span
          ? [
              {
                startMs: span.startMs,
                endMs: span.endMs,
                color: jobStatusBarColor(span.status),
                title: `${span.status} · ${job.jobId || job.id}`,
                ongoing: ['published', 'pending'].includes(span.status),
                jobId: String(job.jobId || job.id),
              },
            ]
          : [],
        points: [],
      }
    })

    const printRows: LaneRow[] = physicalJobs.map((job) => {
      const span = getJobSpan(job)
      const copyLabel = job.splitCopy
        ? `Copy ${(job.copyIndex ?? 0) + 1}/${job.copyTotal || '?'}`
        : 'Print job'
      return {
        key: `print-${job.jobId || job.id}`,
        label: String(job.deviceId || job.jobId || job.id),
        subLabel: copyLabel,
        group: 'print',
        bars: span
          ? [
              {
                startMs: span.startMs,
                endMs: span.endMs,
                color: jobStatusBarColor(span.status),
                title: `${span.status} · ${job.jobId || job.id}`,
                ongoing: ['published', 'pending'].includes(span.status),
                jobId: String(job.jobId || job.id),
              },
            ]
          : [],
        points: [],
      }
    })

    const deviceIds = Array.from(
      new Set(
        printJobs
          .map((job) => String(job.deviceId || ''))
          .filter(Boolean)
          .concat(mishaps.map((m) => String(m.deviceId || '')).filter(Boolean))
      )
    )

    const healthRows: LaneRow[] = deviceIds.map((deviceId) => {
      const deviceMishaps = mishaps.filter((m) => String(m.deviceId || '') === deviceId)
      return {
        key: `health-${deviceId}`,
        label: deviceId,
        subLabel: 'Health',
        group: 'health',
        bars: deviceMishaps
          .map((mishap) => {
            const interval = getMishapInterval(mishap)
            if (!interval) {
              return null
            }
            const color = isOfflineMishap(mishap)
              ? '#b02a37'
              : isStatusMishap(mishap)
              ? severityBarColor(String(mishap.severity) === 'attention' ? 'warning' : 'danger')
              : '#f1416c'
            return {
              startMs: interval.startMs,
              endMs: interval.endMs,
              color,
              title: `${mishap.type || 'mishap'} · ${mishap.reason || mishap.status || ''}`,
              ongoing: !interval.resolved,
            }
          })
          .filter(Boolean) as LaneRow['bars'],
        points: [],
      }
    })

    const allRows = [...pointLanes, ...prefetchRows, ...printRows, ...healthRows].filter(
      (row) => row.bars.length > 0 || row.points.length > 0
    )

    const allTimes = allRows.flatMap((row) => [
      ...row.bars.flatMap((b) => [b.startMs, b.endMs]),
      ...row.points.map((p) => p.ts),
    ])

    if (allTimes.length === 0) {
      const now = Date.now()
      return {rows: allRows, minTs: now - 60_000, maxTs: now, originTs: now - 60_000}
    }

    const min = Math.min(...allTimes)
    const max = Math.max(...allTimes)
    const pad = endAtLastEvent ? Math.max(5_000, (max - min) * 0.02) : Math.max(30_000, (max - min) * 0.05)
    const maxTs = endAtLastEvent ? max + pad : max + pad

    return {rows: allRows, minTs: min - (endAtLastEvent ? pad : pad), maxTs, originTs: min}
  }, [events, printJobs, mishaps, endAtLastEvent])

  const span = Math.max(1, maxTs - minTs)
  const toPercent = (ts: number) => Math.max(0, Math.min(100, ((ts - minTs) / span) * 100))

  const axisTicks = useMemo(() => {
    const count = 4
    return Array.from({length: count}, (_, index) => {
      const ts = minTs + (span * index) / (count - 1)
      return {ts, left: (index / (count - 1)) * 100}
    })
  }, [minTs, span])

  const groupedRows = useMemo(() => {
    const order: LaneRow['group'][] = ['lifecycle', 'prefetch', 'print', 'health']
    return order
      .map((group) => ({
        group,
        meta: GROUP_META[group],
        rows: rows.filter((row) => row.group === group),
      }))
      .filter((section) => section.rows.length > 0)
  }, [rows])

  if (rows.length === 0) {
    return (
      <div className='card card-flush border border-gray-200 mb-8'>
        <div className='card-body py-6'>
          <div className='fw-bold text-gray-800 mb-1'>Content Story Timeline</div>
          <div className='text-muted fw-semibold fs-7'>
            No timed print activity yet. Prefetch and print jobs appear as parallel lanes when
            present.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card card-flush border border-gray-200 mb-8'>
      <div className='card-header border-0 pt-5 pb-0'>
        <div className='card-title flex-column align-items-start'>
          <span className='fw-bold text-gray-800 fs-5'>Content Story Timeline</span>
          <span className='text-muted fs-7 fw-semibold mt-1'>
            Real time scale · timers from first event (T+0ms)
          </span>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex flex-wrap gap-3'>
            {LEGEND.map((item) => (
              <div key={item.label} className='d-flex align-items-center gap-2'>
                <span
                  className='rounded'
                  style={{width: 12, height: 12, backgroundColor: item.color}}
                />
                <span className='text-muted fs-8 fw-semibold'>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className='card-body pt-5'>
        <div className='d-flex justify-content-between text-muted fs-8 mb-4 px-1'>
          <span>
            {formatTimestamp(originTs)} · <span className='fw-bold text-gray-700'>T+0ms</span>
          </span>
          <span>
            {formatTimestamp(maxTs)} ·{' '}
            <span className='fw-bold text-gray-700'>{formatElapsedSince(maxTs, originTs)}</span>
          </span>
        </div>

        <div className='d-flex flex-column gap-6'>
          {groupedRows.map((section) => (
            <div key={section.group}>
              <div className='d-flex align-items-center gap-2 mb-3'>
                <span className={`badge ${section.meta.badge}`}>{section.meta.title}</span>
                <span className='text-muted fs-8'>{section.meta.description}</span>
              </div>

              <div className='d-flex flex-column gap-2'>
                {section.rows.map((row) => {
                  const rowStart =
                    row.bars[0]?.startMs ??
                    (row.points.length
                      ? Math.min(...row.points.map((p) => p.ts))
                      : undefined)
                  const rowElapsed =
                    rowStart !== undefined ? formatElapsedSince(rowStart, originTs) : null

                  return (
                  <div key={row.key} className='d-flex align-items-center gap-3'>
                    <div style={{width: 148, flexShrink: 0}} className='pe-2'>
                      <div className='fw-bold text-gray-800 fs-8 text-truncate' title={row.label}>
                        {row.label}
                      </div>
                      <div className='d-flex align-items-center gap-2'>
                        {row.subLabel && (
                          <div className='text-muted fs-8 text-truncate'>{row.subLabel}</div>
                        )}
                        {rowElapsed && (
                          <span className='badge badge-light-primary fw-bold fs-8'>{rowElapsed}</span>
                        )}
                      </div>
                    </div>

                    <div
                      className='position-relative flex-grow-1 rounded'
                      style={{
                        height: 32,
                        background:
                          'linear-gradient(90deg, rgba(245,248,250,0.9) 0%, rgba(239,242,245,0.95) 100%)',
                        border: '1px solid #eff2f5',
                      }}
                    >
                      {[25, 50, 75].map((mark) => (
                        <span
                          key={mark}
                          className='position-absolute top-0 bottom-0'
                          style={{
                            left: `${mark}%`,
                            width: 1,
                            backgroundColor: 'rgba(161,165,183,0.25)',
                          }}
                        />
                      ))}

                      {row.bars.map((bar, index) => {
                        const left = toPercent(bar.startMs)
                        const width = Math.max(1.2, toPercent(bar.endMs) - left)
                        const startLabel = formatElapsedSince(bar.startMs, originTs)
                        const endLabel = formatElapsedSince(bar.endMs, originTs)
                        const durationLabel = formatDuration(Math.max(0, bar.endMs - bar.startMs))
                        return (
                          <button
                            key={`${row.key}-bar-${index}`}
                            type='button'
                            title={`${bar.title} · ${startLabel} → ${endLabel} (${durationLabel})`}
                            className='position-absolute border-0'
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                              top: 7,
                              height: 18,
                              borderRadius: 6,
                              backgroundColor: bar.color,
                              opacity: bar.ongoing ? 0.75 : 1,
                              boxShadow: bar.ongoing
                                ? `0 0 0 2px rgba(255,255,255,0.9), 0 0 0 3px ${bar.color}`
                                : '0 1px 2px rgba(16,24,40,0.12)',
                              cursor: bar.jobId && onSelectJob ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                              if (bar.jobId && onSelectJob) {
                                onSelectJob(bar.jobId)
                              }
                            }}
                          />
                        )
                      })}

                      {row.points.map((point, index) => (
                        <span
                          key={`${row.key}-pt-${index}`}
                          title={`${point.title} · ${formatElapsedSince(point.ts, originTs)} · ${formatTimestamp(point.ts)}`}
                          className='position-absolute rounded-circle border border-white'
                          style={{
                            left: `calc(${toPercent(point.ts)}% - 6px)`,
                            top: 8,
                            width: 14,
                            height: 14,
                            backgroundColor: point.color,
                            boxShadow: '0 0 0 2px rgba(255,255,255,0.95)',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div className='position-relative mt-4' style={{paddingLeft: 160}}>
          <div className='border-top border-gray-200 pt-2 position-relative' style={{height: 40}}>
            {axisTicks.map((tick) => (
              <div
                key={tick.ts}
                className='position-absolute text-muted fs-8 text-center'
                style={{
                  left: `${tick.left}%`,
                  transform:
                    tick.left === 0
                      ? 'none'
                      : tick.left === 100
                      ? 'translateX(-100%)'
                      : 'translateX(-50%)',
                }}
              >
                <div className='fw-bold text-gray-700'>{formatElapsedSince(tick.ts, originTs)}</div>
                <div>
                  {new Date(tick.ts).toLocaleString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                    month: 'short',
                    day: '2-digit',
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
